import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { Column } from '@tempmee/react';
import { CenteredSpinner } from '~/commonComponents';
import { ICandidateListViewEntity } from '~/domain/PermanentJobs';
import { firePermCandidateListCandidateClicked } from '~/posthogs/events';
import { EmptyState } from '../../../JobOverview/components/CandidateTabView/CandidateListTable/EmptyState';
import { CandidateSection } from './CandidateSection';
import { useCandidateSections } from './useCandidateSections';

interface ICandidatesTabProps {
  jobPostingId: string;
}

/**
 * The Candidates tab of Posting Details: one section per status group, each drawn only
 * while it holds a candidate. No status tabs and no shortlist.
 */
export const CandidatesTab = ({ jobPostingId }: ICandidatesTabProps) => {
  const navigate = useNavigate();
  const { sections, isLoading, isError } = useCandidateSections(jobPostingId);

  // Opens a candidate exactly where today's Candidates tab does: the M2M candidate overview.
  const handleOpenCandidate = useCallback(
    (candidate: ICandidateListViewEntity) => {
      firePermCandidateListCandidateClicked({
        postingId: jobPostingId,
        professionalId: candidate.professional.id,
        candidateStatus: candidate.officeStatus,
      });

      navigate(`/permanent-jobs/${jobPostingId}/candidates/${candidate.professional.id}/overview`);
    },
    [jobPostingId, navigate],
  );

  if (isLoading) {
    return <CenteredSpinner />;
  }

  // The counts failed (the hook has toasted it): there is no telling whether the
  // posting has candidates, so say nothing rather than "No candidates yet".
  if (isError && sections.length === 0) {
    return null;
  }

  if (sections.length === 0) {
    return <EmptyState />;
  }

  return (
    <Column gap={4} className="w-full" data-cy="posting-candidate-sections">
      {sections.map((section) => (
        <CandidateSection
          key={section.key}
          jobPostingId={jobPostingId}
          section={section}
          onOpenCandidate={handleOpenCandidate}
        />
      ))}
    </Column>
  );
};
